import { useLocation, Link } from "react-router-dom";
import { useEffect } from "react";
import { Compass, Home, Phone, Anchor } from "lucide-react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { updateMetaTag } from "@/lib/seo-utils";
import { updateCanonicalTag, getProductionUrl } from "@/lib/canonical-utils";

const NotFound = () => {
  const location = useLocation();

  useEffect(() => {
    console.error("404 Error: User attempted to access non-existent route:", location.pathname);

    // SEO — noindex, canonical to homepage
    document.title = "Page Not Found | ShoreSight Marine";
    updateMetaTag("name", "description", "The page you're looking for doesn't exist. Return to ShoreSight Marine for professional marine window and hatch services in San Diego.");
    updateMetaTag("name", "robots", "noindex, nofollow");
    updateCanonicalTag(getProductionUrl("/"));
    updateMetaTag("property", "og:url", getProductionUrl("/"));
  }, [location.pathname]);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.12, delayChildren: 0.2 },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
  };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Header />

      {/* Hero */}
      <section className="relative flex-1 flex items-center justify-center bg-gradient-to-br from-navy-900 via-navy-800 to-navy-900 py-20 md:py-32">
        {/* Subtle grid pattern */}
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAxMCAwIEwgMCAwIDAgMTAiIGZpbGw9Im5vbmUiIHN0cm9rZT0icmdiYSgyNTUsMjU1LDI1NSwwLjAzKSIgc3Ryb2tlLXdpZHRoPSIxIi8+PC9wYXR0ZXJuPjwvZGVmcz48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSJ1cmwoI2dyaWQpIi8+PC9zdmc+')] opacity-40" />

        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            className="max-w-2xl mx-auto text-center"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            {/* Icon */}
            <motion.div variants={itemVariants}>
              <motion.div
                className="inline-flex items-center justify-center w-24 h-24 rounded-full bg-azure-500/15 border border-azure-500/25 mb-8"
                initial={{ rotate: -20 }}
                animate={{ rotate: 0 }}
                transition={{ duration: 0.6, type: "spring" }}
              >
                <Compass className="w-12 h-12 text-azure-500" />
              </motion.div>
            </motion.div>

            {/* 404 number */}
            <motion.p
              variants={itemVariants}
              className="text-azure-500/60 text-8xl md:text-9xl font-serif font-bold leading-none mb-4 select-none"
            >
              404
            </motion.p>

            {/* Heading */}
            <motion.h1
              variants={itemVariants}
              className="text-3xl md:text-4xl font-serif font-bold text-white mb-4"
            >
              Off Course
            </motion.h1>

            {/* Subtitle */}
            <motion.p
              variants={itemVariants}
              className="text-lg text-gray-300 mb-10 max-w-md mx-auto"
            >
              Looks like this page has drifted out to sea.
              Let's get you back on course.
            </motion.p>

            {/* CTA buttons */}
            <motion.div
              variants={itemVariants}
              className="flex flex-col sm:flex-row gap-4 justify-center"
            >
              <Link to="/">
                <Button
                  size="lg"
                  className="bg-white hover:bg-white/90 text-navy-900 hover:text-navy-800 font-bold shadow-xl hover:shadow-2xl transition-all min-h-[48px] px-8 w-full sm:w-auto"
                >
                  <Home className="mr-2 h-5 w-5" />
                  Return to Homepage
                </Button>
              </Link>
              <Link to="/#contact">
                <Button
                  size="lg"
                  variant="outline"
                  className="bg-transparent hover:bg-white/10 border-2 border-white/80 hover:border-white text-white font-semibold min-h-[48px] px-8 w-full sm:w-auto"
                >
                  <Phone className="mr-2 h-5 w-5" />
                  Contact Us
                </Button>
              </Link>
            </motion.div>

            {/* Decorative anchor */}
            <motion.div
              variants={itemVariants}
              className="mt-16 flex justify-center"
            >
              <Anchor className="w-6 h-6 text-azure-500/30" />
            </motion.div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default NotFound;
